import type Luminary from '@lumen5/luminary';

import { BaseRegistry, type ItemRepresentation } from './BaseRegistry';
import { formatTags } from './formatTags';

import type { AssetContainerModel } from 'js/src/client';
import { ApiService, AssetContainerTypes, TranscriptAssetType } from 'js/src/client';
import { resolveVoiceoverTranscript } from 'js/src/components/sketch/sketch-transcript-utils';
import type { ImmutableAudioAsset } from 'js/src/data/media';
import { ImmutableFromJS } from 'js/src/libs/consts';

interface VoiceoverRepresentation extends ItemRepresentation {
    type: 'audio';
    duration: number;
    has_voiceover: boolean;
    voiceoverTranscript?: string;
}

type AudioAssetWithTranscript = NonNullable<AssetContainerModel['audio']> & {
    _voiceoverTranscript?: string;
};

/**
 * Registry for the user's previously-uploaded voiceover audio files, populated by
 * search_uploaded_voiceovers and consumed by add_recorded_voiceover_scene_group.
 *
 * Unlike MusicRegistry, voiceover UUIDs are resolved via audioassetRetrieve (which returns
 * an AudioAssetModel) rather than the base bulkMediaAssetsList (which only returns background
 * media). This lets the registry resolve any voiceover UUID on demand — including after a
 * conversation reload — so no persisted state is required.
 */
export class VoiceoverRegistry extends BaseRegistry<VoiceoverRepresentation> {
    private static instance: VoiceoverRegistry;

    public static getInstance(): VoiceoverRegistry {
        if (!VoiceoverRegistry.instance) {
            VoiceoverRegistry.instance = new VoiceoverRegistry();
        }
        return VoiceoverRegistry.instance;
    }

    protected validateItemData(data: AssetContainerModel): void {
        if (!data.audio) {
            throw new Error('Invalid voiceover data: must contain audio');
        }
    }

    protected extractItemInfo(data: AssetContainerModel): {
        title: string;
        description: string;
        tags: string[];
    } {
        const audio = data.audio!;
        return {
            title: audio.title || audio.name || 'Untitled',
            description: audio.description || '',
            tags: Array.isArray(audio.tags) ? audio.tags : [],
        };
    }

    protected createRepresentation(
        id: string,
        itemInfo: { title: string; description: string; tags: string[] },
        data: AssetContainerModel,
    ): VoiceoverRepresentation {
        const audio = data.audio! as AudioAssetWithTranscript;
        return {
            id,
            type: 'audio',
            title: itemInfo.title,
            description: itemInfo.description,
            tags: formatTags(itemInfo.tags, itemInfo),
            duration: Math.floor(audio.duration),
            has_voiceover: Boolean(audio.has_voiceover),
            voiceoverTranscript: audio._voiceoverTranscript || undefined,
        };
    }

    protected getItemUuid(data: AssetContainerModel): string | undefined {
        return data.audio?.uuid ?? undefined;
    }

    protected isItemAlreadyInRegistry(uuid: string): string | null {
        for (const id of this.registry.keys()) {
            const entry = this.registry.get(id);
            if (!entry) {
                continue;
            }
            if (entry.data.audio?.uuid === uuid) {
                return id;
            }
        }
        return null;
    }

    /** Returns the audio asset as an ImmutableMap, ready to apply as a voiceover. */
    public getAudioAssetData(id: string): ImmutableAudioAsset | null {
        const entry = this.registry.get(id);
        if (!entry?.data.audio) {
            return null;
        }
        return ImmutableFromJS(entry.data.audio) as ImmutableAudioAsset;
    }

    // eslint-disable-next-line @typescript-eslint/require-await
    public async addAnyUnseenItemsFromVideo(_luminary: Luminary): Promise<void> {
        // Voiceovers are only added via explicit search, not scraped from the video.
    }

    private wrapAudioAsset(audio: AudioAssetWithTranscript): AssetContainerModel {
        return {
            type: AssetContainerTypes.AUDIO,
            audio,
            background: null,
            icon: null,
            font: null,
            document: null,
            document_id: null,
            visual_description: null,
            folder_id: null,
            article_id: null,
        };
    }

    protected override async fetchItemsFromApi(
        unseenUuids: Set<string>,
    ): Promise<AssetContainerModel[]> {
        // There is no bulk audio-asset endpoint, so each UUID needs its own request.
        // Fetch them together: resolveVoiceoverTranscript polls while transcription is
        // still running, so resolving these one at a time stacks those waits.
        const fetches = Array.from(unseenUuids).map(async (uuid) => {
            try {
                const response = await ApiService.audioassetRetrieve({ path: { uuid } });
                const { voiceoverTranscript } = await resolveVoiceoverTranscript(
                    response.data,
                    TranscriptAssetType.AUDIO,
                );
                return this.wrapAudioAsset({
                    ...response.data,
                    _voiceoverTranscript: voiceoverTranscript || undefined,
                });
            } catch {
                // A failed lookup means the UUID is unknown/expired — an expected
                // outcome (the model may pass a stale or hallucinated UUID), not a
                // reportable error. Skip it; the caller surfaces a user-facing message.
                return null;
            }
        });

        const settled = await Promise.all(fetches);
        return settled.filter((asset): asset is AssetContainerModel => asset !== null);
    }

    /**
     * Fetches an uploaded audio asset, resolves whether it has a voiceover (polling for the
     * transcript while transcription is still in progress), and adds it to this registry.
     * Returns the resolved voiceover state so the caller can route non-voiceover audio (e.g.
     * music) elsewhere. Returns null if the asset couldn't be fetched.
     */
    public async addUploadedAudioIfVoiceover(uuid: string): Promise<{
        assetData: AssetContainerModel;
        hasVoiceover: boolean;
        voiceoverTranscript?: string;
        registryId: string | null;
    } | null> {
        let response;
        try {
            response = await ApiService.audioassetRetrieve({ path: { uuid } });
        } catch {
            return null;
        }

        const { hasVoiceover, voiceoverTranscript } = await resolveVoiceoverTranscript(
            response.data,
            TranscriptAssetType.AUDIO,
        );

        const assetData = this.wrapAudioAsset({
            ...response.data,
            _voiceoverTranscript: voiceoverTranscript || undefined,
        });

        // Treat an unresolved state (null) as no voiceover — music, not a voiceover.
        const isVoiceover = hasVoiceover === true;
        const registryId = isVoiceover ? this.addItem(assetData) : null;

        return { assetData, hasVoiceover: isVoiceover, voiceoverTranscript, registryId };
    }
}

export type SerializedVoiceoverRegistry = ReturnType<VoiceoverRegistry['serialize']>;
